import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import BillingAndPayment from "../BillingAndPayment/BillingAndPayment";
import { Helmet } from "react-helmet-async";
import Loader from "../../../../components/Loader";
import {
  createPaymentIntentAction,
  fetchCheckoutDetails,
  resetCheckout,
  selectCheckout,
  setClientSecret,
} from "../../slices/checkoutSlice";
import { AppDispatch } from "../../../../app/store";
import MobileOrderSummary from "../MobileOrderSummary/MobileOrderSummary";
import MobileOrderSummaryHeader from "../MobileOrderSummaryHeader/MobileOrderSummaryHeader";
import OrderSummary from "../OrderSummary/OrderSummary";
import FreeCheckout from "../FreeCheckout/FreeCheckout";

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!,
);

const Checkout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { clientSecret, loading, error, totalDueToday, billingEmail, membership } =
    useSelector(selectCheckout);

  // Checkout details have loaded and nothing is due — the Complimentary (free)
  // tier. Show the free-completion panel instead of waiting on a payment intent.
  const isFreeCheckout = !loading && membership !== null && totalDueToday === 0;

  const [showOrderSummary, setShowOrderSummary] = useState(false);

  useEffect(() => {
    const joinSessionUuid = localStorage.getItem("joinSessionUuid");
    if (joinSessionUuid) {
      dispatch(fetchCheckoutDetails(joinSessionUuid));
    } else {
      console.error("Join session UUID not found");
    }
    // Reset on unmount so returning to checkout (e.g. via browser back) starts
    // clean instead of acting on the previous visit's total/clientSecret.
    return () => {
      dispatch(resetCheckout());
    };
  }, [dispatch]);

  useEffect(() => {
    const joinSessionUuid = localStorage.getItem("joinSessionUuid");
    if (!joinSessionUuid) return;
    if (totalDueToday > 0) {
      const amountInCents = Math.round(totalDueToday * 100);
      dispatch(
        createPaymentIntentAction(joinSessionUuid, amountInCents, billingEmail),
      );
    } else {
      // Total dropped back to $0 (e.g. a donation was removed) — clear the
      // payment intent so the free-completion panel shows instead of the form.
      dispatch(setClientSecret(null));
    }
  }, [dispatch, totalDueToday]);

  const toggleOrderSummary = () => {
    setShowOrderSummary(!showOrderSummary);
  };

  const closeSummary = () => {
    setShowOrderSummary(false);
  };

  const options: any | undefined = clientSecret
    ? {
        clientSecret,
        appearance: { theme: "stripe" },
      }
    : undefined;

  return (
    <>
      <Helmet>
        <title>Checkout - National Society of Collegiate Scholars</title>
      </Helmet>
      {error && <div className="text-red-500 p-4">{error}</div>}
      {clientSecret && totalDueToday > 0 ? (
        <Elements options={options} stripe={stripePromise}>
          <div className="flex flex-col md:flex-row h-screen w-full relative">
            <MobileOrderSummaryHeader
              showOrderSummary={showOrderSummary}
              toggleOrderSummary={toggleOrderSummary}
              totalDueToday={totalDueToday}
            />

            {/* Slide-Down Mobile Order Summary */}
            <MobileOrderSummary
              closeSummary={closeSummary}
              isVisible={showOrderSummary}
            />

            {/* Desktop Order Summary */}
            <div
              className={`hidden md:block bg-primary w-full md:w-1/2 h-screen overflow-auto`}
            >
              <OrderSummary />
            </div>

            {/* Payment Form */}
            <div className="w-full md:w-1/2 h-screen overflow-auto">
              <BillingAndPayment />
            </div>
          </div>
        </Elements>
      ) : isFreeCheckout ? (
        <div className="flex flex-col md:flex-row h-screen w-full relative">
          <MobileOrderSummaryHeader
            showOrderSummary={showOrderSummary}
            toggleOrderSummary={toggleOrderSummary}
            totalDueToday={totalDueToday}
          />

          {/* Slide-Down Mobile Order Summary */}
          <MobileOrderSummary
            closeSummary={closeSummary}
            isVisible={showOrderSummary}
          />

          {/* Desktop Order Summary */}
          <div className="hidden md:block bg-primary w-full md:w-1/2 h-screen overflow-auto">
            <OrderSummary />
          </div>

          {/* Free completion + upgrade upsell */}
          <div className="w-full md:w-1/2 h-screen overflow-auto">
            <FreeCheckout />
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default Checkout;
